import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';

// Import pages
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import EmployeeFormPage from './pages/EmployeeFormPage';
import EmployeeListPage from './pages/EmployeeListPage';
import AuditLogPage from './pages/AuditLogPage';
import AdminAuditTrailPage from './pages/AdminAuditTrailPage';
import ExportPage from './pages/ExportPage';
import SettingsPage from './pages/SettingsPage';
import PositionsManagementPage from './pages/PositionsManagementPage';
import CandidatePoolPage from './pages/CandidatePoolPage';
import ReviewSubmissionPage from './pages/ReviewSubmissionPage';
import CandidateDetailsPage from './pages/CandidateDetailsPage';
import SubmissionsPage from './pages/SubmissionsPage';
import CandidateSubmissionPage from './pages/CandidateSubmissionPage';
import DocumentationPage from './pages/DocumentationPage';
import DraftsPage from './pages/DraftsPage';

// Import components
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: string | string[] }> = ({ 
  children, 
  requiredRole 
}) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    // Admin and viewer can access all pages (viewer has read-only access)
    if (!allowedRoles.includes(user.role) && user.role !== 'admin' && user.role !== 'viewer') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// Public Route component (redirects to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          
          <Route 
            path="/reset-password" 
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } 
          />
          
          {/* Protected routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/register"
            element={
              <Layout>
                <EmployeeFormPage />
              </Layout>
            }
          />
          
          <Route 
            path="/resubmit" 
            element={
              <ProtectedRoute requiredRole="candidate">
                <Layout>
                  <EmployeeFormPage isResubmission={true} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/my-application" 
            element={
              <ProtectedRoute requiredRole="candidate">
                <Layout>
                  <CandidateDetailsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/candidate/submission" 
            element={
              <ProtectedRoute requiredRole="candidate">
                <Layout>
                  <CandidateSubmissionPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/drafts" 
            element={
              <ProtectedRoute requiredRole={['hr_clerk', 'admin', 'viewer']}>
                <Layout>
                  <DraftsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Submissions pages for different statuses */}
          <Route 
            path="/submissions/:status" 
            element={
              <ProtectedRoute>
                <Layout>
                  <SubmissionsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/candidate-pool" 
            element={
              <ProtectedRoute requiredRole="manager">
                <Layout>
                  <CandidatePoolPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/review/:id" 
            element={
              <ProtectedRoute requiredRole="hr_clerk">
                <Layout>
                  <ReviewSubmissionPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Candidate view for HR/Admin to see what the applicant sees */}
          <Route 
            path="/candidate/:id" 
            element={
              <ProtectedRoute requiredRole={["hr_clerk", "admin"]}>
                <Layout>
                  <CandidateDetailsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/employees" 
            element={
              <ProtectedRoute requiredRole="hr_clerk">
                <Layout>
                  <EmployeeListPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/audit/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <AuditLogPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin/audit"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <AdminAuditTrailPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/export" 
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <ExportPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/positions"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <PositionsManagementPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route 
            path="/documentation" 
            element={
              <ProtectedRoute>
                <Layout>
                  <DocumentationPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* 404 route */}
          <Route 
            path="*" 
            element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-gray-600 mb-8">Page not found</p>
                  <button 
                    onClick={() => window.history.back()}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            } 
          />
        </Routes>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </div>
    </Router>
  );
};

export default App;
